import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { EmptyState } from './EmptyState'
import { Loader } from './Loader'

export interface Column<T> {
  key: string
  header: string
  cell: (row: T) => React.ReactNode
  className?: string
}

interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  loading?: boolean
  error?: string | null
  emptyIcon?: React.ReactNode
  emptyTitle?: string
  emptyDescription?: string
  footer?: React.ReactNode
}

import type React from 'react'

export function DataTable<T>({
  columns,
  data,
  loading,
  error,
  emptyIcon,
  emptyTitle = 'Sin resultados',
  emptyDescription,
  footer,
}: DataTableProps<T>) {
  if (loading) return <Loader />

  if (error) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border-secondary bg-bg-error-secondary px-4 py-3 text-sm text-text-error">
        {error}
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        description={emptyDescription}
      />
    )
  }

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl border border-border-secondary bg-bg-primary shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key} className={col.className}>
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, i) => (
              <TableRow key={i}>
                {columns.map((col) => (
                  <TableCell key={col.key} className={col.className}>
                    {col.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {footer && <div>{footer}</div>}
    </div>
  )
}
